import React, { useMemo } from "react";
import styles from "./index.module.scss";
import { AvailableGiftCardItem, DISCOUNT_STATUS } from "../../../../constants";
import classNames from "classnames";
import copy from "copy-to-clipboard";
import { sendMessageToParent } from "../../../../utils";
import { IDiscountItem } from "../../../../types";

interface GiftCardItemProps {
  data: IDiscountItem;
  availableGiftCards: AvailableGiftCardItem[];
}

export interface IGiftCardItem {
  create_time: string;
  id: number;
  shopify_customer_id: number;
  shopify_gift_card_code: string;
  shopify_gift_card_id: number;
  source_type: number;
  status: number;
  update_time: string;
}

const GiftCardItem: React.FC<GiftCardItemProps> = ({
  data,
  availableGiftCards,
}) => {
  const isGiftCardAvailable = useMemo(() => {
    return data.status === DISCOUNT_STATUS.AVAILABLE;
  }, [data.status]);

  const shouldAddRewardText = useMemo(() => {
    return data.shopify_discount_code.startsWith("MR");
  }, [data.shopify_discount_code]);

  const handleCopy = () => {
    if (isGiftCardAvailable) {
      const isCopied = copy(data.shopify_discount_code, {
        debug: false,
        format: "text/plain",
      });

      isCopied &&
        sendMessageToParent({
          type: "member-toast",
          data: {
            type: "success",
            message: "Copied successfully",
            duration: 3000,
          },
        });
    } else {
      sendMessageToParent({
        type: "member-toast",
        data: {
          type: "error",
          message: "Gift card used",
          duration: 3000,
        },
      });
    }
  };

  const card = useMemo(() => {
    return availableGiftCards.find((card) => card.type === data.redeem_type);
  }, [availableGiftCards, data.redeem_type]);

  return (
    <div key={data.id} className={styles.giftCardItem}>
      <div className={styles.giftCardLabel}>
        <span>Member Reward</span>
      </div>
      <div className={styles.giftCardContent}>
        <div className={styles.leftSection}>
          <div className={styles.amount}>{card?.amountText}</div>

          {shouldAddRewardText && (
            <div className={styles.rewardText}>
              Combinable with other coupons
            </div>
          )}

          <div
            className={classNames(styles.points, {
              [styles.gray]: shouldAddRewardText,
            })}
          >{`Redeemed By ${card?.points} points`}</div>
          <div className={styles.codes}>Code:{data.shopify_discount_code}</div>
        </div>
        <div className={styles.rightSection}>
          <button
            data-element-id={`copy-gift-card-code`}
            className={classNames(styles.copyButton, {
              [styles.disabled]: !isGiftCardAvailable,
            })}
            onClick={handleCopy}
          >
            Copy
          </button>
        </div>
      </div>
    </div>
  );
};

export default GiftCardItem;
